import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { ChevronDown, ChevronLeft, ChevronRight, Hourglass, Layers, List, Tag, UserCheck } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'

import useLocaleText from 'components/useLocaleText'
import { DropdownMenu, EmptyState, IDropdownOption, SearchInput } from 'components/ui'
import { AppDispatch } from 'config/redux'
import { RootState, Task } from 'constants/interfaces'
import colors from 'constants/colors'
import keys from 'constants/keys'
import { getTask, patchTaskOverviewModal, setTaskOverviewModal } from 'redux/actions/tasks_v2'
import { isRtlSelector } from 'redux/selectors/general'

import { TaskPreviewContextList } from 'components/NewTaskDashboard/TaskPreviewContextList'
import {
  buildStatusChips,
  StatusFilterId,
  TaskPreviewStatusTabs,
} from 'components/NewTaskDashboard/TaskPreviewStatusTabs'
import { ContextGroupMode } from 'components/NewTaskDashboard/types'
import { useContextGrouping } from 'components/NewTaskDashboard/useContextGrouping'
import { useVisibleContextComments } from 'components/NewTaskDashboard/useVisibleContextComments'
import { useOwnerTaskInsights } from './useOwnerTaskInsights'
import { TASK_STATUS_ID_BY_VALUE } from 'utils/taskStatusModel'

const GROUP_MODE_OPTIONS: IDropdownOption<ContextGroupMode>[] = [
  { id: 'all', value: 'all', label: 'All contexts', icon: <List size={13} /> },
  { id: 'groups', value: 'groups', label: 'By Groups', icon: <Layers size={13} /> },
  { id: 'tags', value: 'tags', label: 'By Tags', icon: <Tag size={13} /> },
  { id: 'auditors', value: 'auditors', label: 'Auditors', icon: <UserCheck size={13} />, disabled: true },
]

const GROUP_MODE_LABELS: Record<ContextGroupMode, string> = {
  all: 'All',
  groups: 'Groups',
  tags: 'Tags',
  auditors: 'Auditors',
}

const statusMatchesContext = (filterId: StatusFilterId, status: number) => TASK_STATUS_ID_BY_VALUE[status] === filterId

interface IProps {
  task: Task
  onBack: () => void
}

export const OwnerTaskInsightsView: React.FC<IProps> = ({ task, onBack }) => {
  const dispatch: AppDispatch = useDispatch()
  const isRtl = useSelector(isRtlSelector)

  const t_search = useLocaleText('search')
  const t_back = useLocaleText('back_button')
  const t_pending_title = useLocaleText('insights_pending_title')
  const t_pending_desc = useLocaleText('insights_pending_desc')

  const { contexts, isLoading, isAwaitingInsights, activeRecurrenceTs } = useOwnerTaskInsights(task)

  const [selectedStatusFilter, setSelectedStatusFilter] = useState<StatusFilterId | null>(null)
  const [contextSearch, setContextSearch] = useState('')
  const [groupMode, setGroupMode] = useState<ContextGroupMode>('all')
  const [selectedSectionKey, setSelectedSectionKey] = useState<string | null>(null)
  const [openingContextId, setOpeningContextId] = useState<string | null>(null)

  // Reset all filters when switching to a different task
  useEffect(() => {
    setSelectedStatusFilter(null)
    setContextSearch('')
    setGroupMode('all')
    setSelectedSectionKey(null)
  }, [task.tid])

  const isAiTask = task.type === keys.TASK_V2_TYPES.AI_TASK
  const isVmTask = task.type === keys.TASK_V2_TYPES.VM_TASK

  const visibleContexts = selectedStatusFilter
    ? contexts.filter((context) => statusMatchesContext(selectedStatusFilter, context.status))
    : contexts

  const statusChips = useMemo(() => buildStatusChips(contexts, { isAiTask, isVmTask }), [contexts, isAiTask, isVmTask])
  const selectedStatusLabel = useMemo(
    () => statusChips.find((chip) => chip.id === selectedStatusFilter)?.meta.label,
    [statusChips, selectedStatusFilter]
  )

  const { getCounterInfo, registerContextNode } = useVisibleContextComments(
    visibleContexts,
    `${task.tid}:${activeRecurrenceTs || ''}`
  )

  const groupSections = useContextGrouping(visibleContexts, groupMode)

  const handleOpenContext = useCallback(
    async (contextId: string, taskContextId: string) => {
      setOpeningContextId(contextId)
      dispatch(
        setTaskOverviewModal({
          tid: task.tid,
          task_context_id: taskContextId,
          result_context_id: contextId,
          recurrence_ts: activeRecurrenceTs,
          isLoading: true,
          error: null,
        })
      )
      try {
        const response = await dispatch(
          getTask({
            tid: task.tid,
            task_context_id: taskContextId,
            result_context_id: contextId,
            recurrence_ts: activeRecurrenceTs,
          })
        )
        dispatch(patchTaskOverviewModal({ isLoading: false, error: response.task ? null : 'Unable to load task' }))
      } catch (e) {
        console.error(e)
        dispatch(patchTaskOverviewModal({ isLoading: false, error: 'Unable to load task' }))
      } finally {
        setOpeningContextId(null)
      }
    },
    [dispatch, task.tid, activeRecurrenceTs]
  )

  return (
    <>
      <Flex h="100%" minH={0} bg="white" flexDir="column" overflow="hidden">
        {/* Back breadcrumb + task title */}
        <Box px={{ base: '16px', md: '20px' }} pt="16px" pb="10px" flexShrink={0}>
          <Flex
            as="button"
            align="center"
            bg="transparent"
            border="none"
            p="0"
            mb="10px"
            cursor="pointer"
            color={colors.greyDark}
            fontSize="12px"
            fontWeight="700"
            onClick={onBack}
            style={{ gap: '4px' }}
          >
            {isRtl ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            <span>{t_back || 'Back'}</span>
          </Flex>
          <Text fontSize="18px" fontWeight="bold" color={colors.blackLight} isTruncated>
            {task.title}
          </Text>
        </Box>

        {isAwaitingInsights ? (
          <Flex flex={1} minH={0} align="center" justify="center" px={{ base: '16px', md: '20px' }} pb="24px">
            <EmptyState
              icon={<Hourglass size={40} strokeWidth={1.5} color="#CBD5E1" />}
              defaultText={t_pending_title || 'Insights are being prepared'}
              description={t_pending_desc || 'It can take a moment after a task is created. Please check back shortly.'}
            />
          </Flex>
        ) : (
          <>
            <Box px={{ base: '16px', md: '20px' }} flexShrink={0}>
              <TaskPreviewStatusTabs
                isLoading={isLoading}
                contexts={contexts}
                statusChips={statusChips}
                selectedStatusFilter={selectedStatusFilter}
                onSelectFilter={setSelectedStatusFilter}
              />

              <Flex pt="8px" pb="6px" align="center" justify="space-between" style={{ gap: '8px' }}>
                <Box flex={1} minW={0} maxW="400px">
                  <SearchInput
                    value={contextSearch}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContextSearch(e.target.value)}
                    onClear={() => setContextSearch('')}
                    placeholder={t_search || 'Search'}
                    variant="filled"
                    bg="#F8FAFC"
                    border="1px solid #E2E8F0"
                    elementHeight="30px"
                    direction={isRtl ? 'rtl' : 'ltr'}
                  />
                </Box>
                <Box flexShrink={0}>
                  <DropdownMenu
                    options={GROUP_MODE_OPTIONS}
                    selectedId={groupMode}
                    onSelect={(opt) => {
                      setGroupMode(opt.value)
                      setSelectedSectionKey(null)
                    }}
                    triggerVariant="filter"
                    triggerIsActive={groupMode !== 'all'}
                    triggerContent={
                      <Flex align="center" style={{ gap: '4px' }}>
                        {GROUP_MODE_OPTIONS.find((o) => o.id === groupMode)?.icon}
                        <span>{GROUP_MODE_LABELS[groupMode]}</span>
                        <ChevronDown size={10} />
                      </Flex>
                    }
                    triggerButtonProps={{ h: '30px', px: '8px', fontSize: '11px', fontWeight: '600' }}
                    placement={isRtl ? 'bottom-start' : 'bottom-end'}
                  />
                </Box>
              </Flex>
            </Box>

            <Box flex={1} minH={0} px={{ base: '16px', md: '20px' }} pb="16px" display="flex" flexDirection="column">
              <TaskPreviewContextList
                isLoading={isLoading}
                visibleContexts={visibleContexts}
                groupMode={groupMode}
                groupModeLabel={GROUP_MODE_OPTIONS.find((o) => o.id === groupMode)?.label as string}
                groupSections={groupSections}
                selectedSectionKey={selectedSectionKey}
                openingContextId={openingContextId}
                selectedStatusLabel={selectedStatusLabel}
                isRtl={isRtl}
                isAiTask={isAiTask}
                contextSearch={contextSearch}
                onSelectSection={setSelectedSectionKey}
                onBack={() => {
                  setSelectedSectionKey(null)
                  setContextSearch('')
                }}
                getCounterInfo={getCounterInfo}
                registerContextNode={registerContextNode}
                onOpenContext={handleOpenContext}
              />
            </Box>
          </>
        )}
      </Flex>

    </>
  )
}
